import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { useEffect, useMemo, useState } from "react";
import BigNumber from "bignumber.js";
import useOrders from "./useOrders";
import { useDatesContext } from "context/DatesContext";

export const useOrderSummary = ({ storeId, type = "fac", start, end }) => {
  const { orderSummary, loading } = useAllOrderSummary(
    storeId,
    type,
    start,
    end
  );
  //
  return {
    loading,
    orderSummary,
  };
};

export const useOrderPaymentSummary = ({
  storeId,
  type = "fac",
  start,
  end,
}) => {
  const { orderPaymentSummary, loading } = useAllOrderPaymentSummary(
    storeId,
    type,
    start,
    end
  );
  return {
    loading,
    orderPaymentSummary,
  };
};

export const useOrderUserSummary = ({
  storeId,
  type = "fac",
  start,
  end,
  limit,
  sort,
}) => {
  const { orderUserSummary, loading } = useAllOrderUserSummary(
    storeId,
    type,
    start,
    end,
    limit,
    sort
  );
  return {
    loading,
    orderUserSummary,
  };
};

function useAllOrderSummary(storeId, type, start, end) {
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrderSummary", 60 * 1000);
  // console.log("fetchPolicy", fetchPolicy);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderSummary($orderSummaryInput: OrderSummaryInput!) {
        orderSummary(input: $orderSummaryInput) {
          _id {
            start
            end
            type
          }
          total
          extraAmount1
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderSummaryInput: {
          storeId,
          type,
          start,
          end,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  const orderSummary = useMemo(() => data?.orderSummary ?? [], [data]);

  return { orderSummary, loading };
}

function useAllOrderPaymentSummary(storeId, type, start, end) {
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrderPaymentSummary", 60 * 1000);
  // console.log("fetchPolicy", fetchPolicy);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderPaymentSummary(
        $orderPaymentSummaryInput: OrderPaymentSummaryInput!
      ) {
        orderPaymentSummary(input: $orderPaymentSummaryInput) {
          _id {
            start
            end
            type
            code
            description
          }

          total
          count
        }
      }
    `,
    {
      variables: {
        orderPaymentSummaryInput: {
          storeId,
          type,
          start,
          end,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  const orderPaymentSummary = useMemo(
    () => data?.orderPaymentSummary ?? [],
    [data]
  );

  const sortedOrderPaymentSummary = useMemo(() => {
    return orderPaymentSummary
      .slice()
      .sort((a, b) => new Date(b._id.start) - new Date(a._id.start));
  }, [orderPaymentSummary]);

  return { orderPaymentSummary: sortedOrderPaymentSummary, loading };
}

function useAllOrderUserSummary(storeId, type, start, end, limit, sort) {
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrderUserSummary", 60 * 1000);
  // console.log("fetchPolicy", fetchPolicy);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderUserSummary(
        $orderUserSummaryInput: OrderUserSummaryInput!
      ) {
        orderUserSummary(input: $orderUserSummaryInput) {
          _id {
            start
            end
            type
            user
          }
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderUserSummaryInput: {
          storeId,
          type,
          start,
          end,
          limit,
          sort,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  let orderUserSummary = useMemo(() => data?.orderUserSummary ?? [], [data]);

  if (!Array.isArray(orderUserSummary)) {
    orderUserSummary = [orderUserSummary];
  }

  const sortedOrderUserSummary = useMemo(() => {
    return orderUserSummary
      .slice()
      .sort((a, b) => new Date(b._id.start) - new Date(a._id.start));
  }, [orderUserSummary]);

  return { orderUserSummary: sortedOrderUserSummary, loading };
}

const dbToCurrency = (value) => new BigNumber(value.toString());

const DECIMAL_ZERO = new BigNumber(0);

function addToConsolidatedData(consolidatedDataGroup, consolidatedDataToAdd) {
  let consolidatedData = consolidatedDataGroup[consolidatedDataToAdd.key];
  if (!consolidatedData) {
    consolidatedData = {
      ...consolidatedDataToAdd,
      total2: consolidatedDataToAdd.total2 || DECIMAL_ZERO,
      quantity2: consolidatedDataToAdd.quantity2 || DECIMAL_ZERO,
      total3: consolidatedDataToAdd.total3 || DECIMAL_ZERO,
      quantity3: consolidatedDataToAdd.quantity3 || DECIMAL_ZERO,
    };
    consolidatedDataGroup[consolidatedData.key] = consolidatedData;
  } else {
    //
    consolidatedData.total = consolidatedData.total.plus(
      consolidatedDataToAdd.total
    );
    consolidatedData.quantity = consolidatedData.quantity.plus(
      consolidatedDataToAdd.quantity
    );
    if (consolidatedDataToAdd.total2) {
      consolidatedData.total2 = consolidatedData.total2.plus(
        consolidatedDataToAdd.total2
      );
    }
    if (consolidatedDataToAdd.quantity2) {
      consolidatedData.quantity2 = consolidatedData.quantity2.plus(
        consolidatedDataToAdd.quantity2
      );
    }
    if (consolidatedDataToAdd.total3) {
      consolidatedData.total3 = consolidatedData.total3.plus(
        consolidatedDataToAdd.total3
      );
    }
    if (consolidatedDataToAdd.quantity3) {
      consolidatedData.quantity3 = consolidatedData.quantity3.plus(
        consolidatedDataToAdd.quantity3
      );
    }
    consolidatedData.count += consolidatedDataToAdd.count;
  }
}

function addTaxData(
  order,
  sign,
  number,
  orderTaxes,
  orderTaxesUser,
  orderTaxesUserDetailed
) {
  const taxId = order[`tax${number}Id`];
  const taxName = order[`tax${number}Name`];
  const tax = order[`tax${number}`];
  const taxImp = order[`tax${number}Imp`];
  if (taxId && taxName && tax && taxImp) {
    const key = taxId.toString();
    const taxValue = dbToCurrency(tax).multipliedBy(sign);
    const taxImpValue = dbToCurrency(taxImp).multipliedBy(sign);
    const taxTotalValue = taxValue.plus(taxImpValue);
    addToConsolidatedData(orderTaxes, {
      key,
      name: taxName,
      count: 1,
      quantity: DECIMAL_ZERO,
      total: taxValue,
      total2: taxImpValue,
      total3: taxTotalValue,
    });
    //
    const userKey = order.billingUserName || "?";
    addToConsolidatedData(orderTaxesUser, {
      key: userKey,
      name: userKey,
      count: 1,
      quantity: DECIMAL_ZERO,
      total: taxValue,
      total2: taxImpValue,
      total3: taxTotalValue,
    });
    const userDetailKey = `${key}-${userKey}`;
    addToConsolidatedData(orderTaxesUserDetailed, {
      key: userDetailKey,
      name: `${taxName} - ${userKey}`,
      count: 1,
      quantity: DECIMAL_ZERO,
      total: taxValue,
      total2: taxImpValue,
      total3: taxTotalValue,
    });
  }
}

function ConsolidatedDataToArray(consolidatedDataGroup) {
  return Object.keys(consolidatedDataGroup).map(
    (key) => consolidatedDataGroup[key]
  );
}

export function useOrderTaxSummary(storeId, range) {
  const [isLoading, setIsLoading] = useState(false);
  const { orders, loading } = useOrders(storeId, range);

  const [data, setData] = useState({
    orderTaxes: [],
    orderTaxesUser: [],
    orderTaxesUserDetailed: [],
  });
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      try {
        const orderTaxes = {};
        const orderTaxesUser = {};
        const orderTaxesUserDetailed = {};

        orders.forEach((order) => {
          const sign = order.type === "cre" ? -1 : order.type === "vou" ? 0 : 1;

          addTaxData(
            order,
            sign,
            1,
            orderTaxes,
            orderTaxesUser,
            orderTaxesUserDetailed
          );
          addTaxData(
            order,
            sign,
            2,
            orderTaxes,
            orderTaxesUser,
            orderTaxesUserDetailed
          );
          addTaxData(
            order,
            sign,
            3,
            orderTaxes,
            orderTaxesUser,
            orderTaxesUserDetailed
          );
          addTaxData(
            order,
            sign,
            4,
            orderTaxes,
            orderTaxesUser,
            orderTaxesUserDetailed
          );
        });

        setData({
          orderTaxes: ConsolidatedDataToArray(orderTaxes),
          orderTaxesUser: ConsolidatedDataToArray(orderTaxesUser),
          orderTaxesUserDetailed: ConsolidatedDataToArray(
            orderTaxesUserDetailed
          ),
        });
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }, 0);
  }, [orders]);
  return { loading: loading || isLoading, data };
}
